// lib/moderacja/kolejka.ts — ETYKIETY KOLEJKI PRZEGLĄDU (Administracja → Moderacja menu).
//
// Przeniesione 1:1 Z FUNKCJI ze starego panelu admina (`_helpers/kolejka-przegladu.ts`, „Menu"),
// bez zmian reguł: te same kategorie, to samo pierwszeństwo, ten sam próg pewności. Zmienił się
// tylko zapis (polskie znaki) — wygląd starego panelu nie jest tu wzorcem, jego działanie tak.
import type { PropozycjaZmianyDania } from '@/lib/api/analytics';

export const ROZMIAR_STRONY_MODERACJI = 20;

/** Rodzaj zgłoszenia oznaczający NOWE DANIE. Wszystko inne (`Update`) to edycja istniejącego. */
export const RODZAJ_NOWE_DANIE = 'Creation';

/** Próg pewności automatu — poniżej niego model nie zatwierdza sam. */
export const PROG_PEWNOSCI_AUTOMATU = 0.8;

/** Słownik znaczników ryzyka z API (nazwy enumu) → tekst dla operatora. */
const ZNACZNIKI: Record<string, string> = {
  PriceAnomaly: 'nietypowa zmiana ceny',
  Profanity: 'wulgaryzmy',
  OffensiveContent: 'treść obraźliwa',
  RestaurantBrandRisk: 'ryzyko dla marki restauracji',
  TuszamaBrandRisk: 'ryzyko dla marki TuSzama',
  PromptInjection: 'próba sterowania modelem',
  DuplicateName: 'duplikat nazwy',
  Ambiguous: 'niejednoznaczne',
  Other: 'inne',
};

const ZNACZNIKI_TEKSTU = ['Profanity', 'OffensiveContent', 'RestaurantBrandRisk', 'TuszamaBrandRisk', 'PromptInjection'];

/** Wartości `imageAssessment`, które oznaczają PROBLEM ze zdjęciem (nie „posiłek" i nie „nie analizowano"). */
const OCENY_ZDJECIA_PROBLEM = ['NotMeal', 'Inappropriate', 'Ambiguous', 'Unavailable', 'Error'];

type Wpis = Pick<PropozycjaZmianyDania,
  'aiDecision' | 'aiConfidence' | 'aiRiskFlags' | 'aiReason' | 'imageAssessment' | 'imageAssessmentLabel' | 'imageReason'
  | 'notificationDelivered' | 'mealDeleted' | 'mealConfirmed'>;

type ZmianaDania = Pick<PropozycjaZmianyDania,
  'kind' | 'oldName' | 'newName' | 'oldDescription' | 'newDescription' | 'oldIngredients' | 'newIngredients'
  | 'oldPrice' | 'newPrice' | 'oldImageUrl' | 'imageUrl'>;

/** Znaczniki ryzyka: API podaje je jako tekst „A, B"; tablicę też przyjmujemy (bez zgadywania kształtu). */
export function znacznikiRyzyka(flagi: string | string[] | null | undefined): string[] {
  const lista = Array.isArray(flagi) ? flagi : String(flagi ?? '').split(',');
  return lista.map((x) => String(x).trim()).filter((x) => x.length > 0);
}

/** Kod błędu dostawcy z końca powodu („… [NO_CONTENT]"), z pominięciem dopisku o kanale przeglądu. */
export function kategoriaBledu(aiReason: string | null | undefined): string | null {
  if (!aiReason) return null;
  const bezKanalu = aiReason.replace(/\s*\|\s*kanal przegladu:.*$/, '');
  const m = /\[([A-Z_]+)\]\s*$/.exec(bezKanalu);
  return m ? m[1]! : null;
}

/** Czytelny powód skierowania do człowieka: decyzja/awaria AI + niska pewność + znaczniki ryzyka. */
export function opisPowodu(wpis: Wpis): string {
  const czesci: string[] = [];
  const blad = kategoriaBledu(wpis.aiReason);
  if (!wpis.aiDecision) czesci.push('brak oceny AI');
  else if (blad) czesci.push(`AI nie odpowiedziało poprawnie (${blad})`);
  else if (wpis.aiDecision === 'AutoApprove') czesci.push('AI: zatwierdź, ale nie spełniono warunków automatu');
  else czesci.push('AI: do człowieka');
  if (wpis.aiDecision && wpis.aiConfidence !== null && wpis.aiConfidence !== undefined && wpis.aiConfidence < PROG_PEWNOSCI_AUTOMATU) {
    czesci.push(`pewność ${wpis.aiConfidence}`);
  }
  const znaczniki = znacznikiRyzyka(wpis.aiRiskFlags).map((z) => ZNACZNIKI[z] ?? z);
  if (znaczniki.length > 0) czesci.push(znaczniki.join(', '));
  return czesci.join(' · ');
}

/** Stan powiadomienia operatora: doszło / nie było dokąd / nieudane. */
export function etykietaPowiadomienia(wpis: Pick<Wpis, 'notificationDelivered' | 'aiReason'>): string {
  if (wpis.notificationDelivered) return 'wysłano';
  const powod = wpis.aiReason ?? '';
  if (powod.includes('kanal przegladu: BRAK_KONFIGURACJI')) return 'brak kanału (NO_DESTINATION)';
  if (powod.includes('kanal przegladu: WYSYLKA_NIEUDANA')) return 'wysyłka nieudana';
  return 'nie wysłano';
}

/** Stan dania, którego dotyczy propozycja. */
export function etykietaStanuDania(wpis: Pick<Wpis, 'mealDeleted' | 'mealConfirmed'>): string {
  if (wpis.mealDeleted) return 'USUNIĘTE';
  if (wpis.mealConfirmed === true) return 'opublikowane';
  if (wpis.mealConfirmed === false) return 'odrzucone';
  return 'nieopublikowane';
}

/** Czas oczekiwania w godzinach → „3 h" / „2 d 5 h". */
export function etykietaOczekiwania(godziny: number | null | undefined): string {
  if (godziny === null || godziny === undefined || Number.isNaN(godziny) || godziny < 0) return '—';
  const dni = Math.floor(godziny / 24);
  const h = godziny % 24;
  return dni > 0 ? `${dni} d ${h} h` : `${h} h`;
}

/** Zatwierdzenie jest niemożliwe dla usuniętego dania — system główny i tak odmówi; przycisk ma to pokazać wcześniej. */
export function wolnoZatwierdzic(wpis: Pick<Wpis, 'mealDeleted'>): boolean {
  return !wpis.mealDeleted;
}

/** „Dodanie" albo „Edycja". */
export function etykietaRodzaju(kind: string | null | undefined): string {
  return kind === RODZAJ_NOWE_DANIE ? 'Dodanie' : 'Edycja';
}

/** Krótki opis różnicy do wiersza tabeli: co się zmienia (nazwa / opis / składniki / cena / zdjęcie). */
export function opisZmiany(wpis: ZmianaDania): string {
  if (wpis.kind === RODZAJ_NOWE_DANIE) return 'nowe danie';
  const zmiany: string[] = [];
  if ((wpis.oldName ?? '') !== (wpis.newName ?? '')) zmiany.push('nazwa');
  if ((wpis.oldDescription ?? '') !== (wpis.newDescription ?? '')) zmiany.push('opis');
  if ((wpis.oldIngredients ?? '') !== (wpis.newIngredients ?? '')) zmiany.push('składniki');
  if (wpis.oldPrice !== wpis.newPrice) zmiany.push('cena');
  if ((wpis.oldImageUrl ?? '') !== (wpis.imageUrl ?? '')) zmiany.push('zdjęcie');
  return zmiany.length > 0 ? zmiany.join(', ') : 'bez różnic';
}

/** Czy ocena zdjęcia (a nie tekst czy cena) jest powodem, dla którego wpis leży w kolejce. */
export function czyProblemZdjecia(wpis: Pick<Wpis, 'imageAssessment'>): boolean {
  return OCENY_ZDJECIA_PROBLEM.includes(String(wpis.imageAssessment ?? ''));
}

export type KodKategoriiPowodu = 'IMAGE' | 'TEXT' | 'DUPLICATE' | 'PRICE' | 'FORMAT' | 'LOW_CONF' | 'OTHER' | 'BRAK';

/**
 * JEDNA kategoria powodu, wg pierwszeństwa starego panelu:
 * BRAK OCENY > ZDJĘCIE > TEKST > DUPLIKAT > CENA > AWARIA ODPOWIEDZI AI > NISKA PEWNOŚĆ > INNE.
 */
export function kategoriaPowodu(wpis: Wpis): { kod: KodKategoriiPowodu; etykieta: string } {
  if (!wpis.aiDecision) return { kod: 'BRAK', etykieta: 'brak oceny AI' };
  if (czyProblemZdjecia(wpis)) return { kod: 'IMAGE', etykieta: 'ZDJĘCIE' };
  const znaczniki = znacznikiRyzyka(wpis.aiRiskFlags);
  if (znaczniki.some((z) => ZNACZNIKI_TEKSTU.includes(z))) return { kod: 'TEXT', etykieta: 'TEKST' };
  if (znaczniki.includes('DuplicateName')) return { kod: 'DUPLICATE', etykieta: 'DUPLIKAT' };
  if (znaczniki.includes('PriceAnomaly')) return { kod: 'PRICE', etykieta: 'CENA' };
  if (kategoriaBledu(wpis.aiReason)) return { kod: 'FORMAT', etykieta: 'AWARIA ODPOWIEDZI AI' };
  if (wpis.aiConfidence !== null && wpis.aiConfidence !== undefined && wpis.aiConfidence < PROG_PEWNOSCI_AUTOMATU) {
    return { kod: 'LOW_CONF', etykieta: 'NISKA PEWNOŚĆ' };
  }
  return { kod: 'OTHER', etykieta: 'INNE' };
}

/** Pewność modelu w procentach („72 %"); brak = pusty łańcuch. */
export function pewnoscProcent(wpis: Pick<Wpis, 'aiConfidence'>): string {
  if (wpis.aiConfidence === null || wpis.aiConfidence === undefined || Number.isNaN(Number(wpis.aiConfidence))) return '';
  return `${Math.round(Number(wpis.aiConfidence) * 100)} %`;
}

/** Werdykt o zdjęciu: etykieta z API + powód modelu (gdy jest). */
export function opisZdjecia(wpis: Pick<Wpis, 'imageAssessmentLabel' | 'imageAssessment' | 'imageReason'>): string {
  const etykieta = wpis.imageAssessmentLabel || String(wpis.imageAssessment ?? '');
  const powod = wpis.imageReason ? ` — ${wpis.imageReason}` : '';
  return etykieta ? `Zdjęcie: ${etykieta}${powod}` : '';
}

/** Ile pozycji kolejki to NOWE DANIA (znacznik „!" starego panelu) — edycje go nie podnoszą. */
export function liczbaNowychDan(wpisy: readonly Pick<PropozycjaZmianyDania, 'kind'>[] | null | undefined): number {
  return (wpisy ?? []).filter((w) => w?.kind === RODZAJ_NOWE_DANIE).length;
}

/** Cena z API jest w groszach; ekran pokazuje złote z dwoma miejscami („21,50"). */
export function cenaZlote(grosze: number | null | undefined): string {
  if (grosze === null || grosze === undefined || Number.isNaN(grosze)) return '—';
  return (grosze / 100).toFixed(2).replace('.', ',');
}
